import React from 'react';
import { Link } from 'react-router-dom';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import Message from '@app/component/message/Message';
import { PlusIcon } from '@heroicons/react/20/solid';
import PricingRoom from '@app/model/pricing-automation/PricingRoom';
import PricingArchitecturePlaceholder
	from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingArchitecturePlaceholder';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import _isEmpty from 'lodash/isEmpty';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import PricingArchitectureConfigurationModal
	from '@app/site/secured/rateshopper/pricing-automation/architecture/overlay/PricingArchitectureConfigurationModal';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import PricingArchitectureFragment
	from '@app/site/secured/rateshopper/pricing-automation/architecture/fragment/PricingArchitectureFragment';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import Switcher from '@app/component/switcher/Switcher';
import {
	useSaveCompanyHotelPriceRecommendationSettingsMutation,
} from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import { useSaveHiddenPricingRoomRateTypesMutation } from '@app/model/company_hotel/service/HiddenPricingRoomRateTypesHooks';
import ToggleChip from '@ui/tag/ToggleChip';
import PricingRoomName from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingRoomName';

const hasText = (value?: string | null): value is string => value !== undefined && value !== null && value !== '';

const PricingRoomAnchoringPage = React.memo(() => {
	return <div className="mx-auto mt-5 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
		<div className="mb-3">
			<h2 className="text-base font-semibold text-gray-900">Room Anchoring</h2>
			<p className="text-sm text-gray-500">
				Map your room types to your PMS inventory.
			</p>
		</div>
		<PricingRoomAnchoringLoader />
	</div>;
});

const PricingRoomAnchoringLoader = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	const results = useQueries({
		queries: [
			{
				queryKey: PricingAutomationQueryKeys.notAssignedPricingRooms(companyHotel.id),
				queryFn: () => PricingAutomationService.findCompanyHotelNotAssignedPricingRooms(companyHotel.id),
				enabled: (companyHotel?.id ?? 0) !== 0,
			},
			{
				queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id),
				queryFn: () => PricingAutomationService.findCompanyHotelPricingArchitectures(companyHotel.id),
				enabled: (companyHotel?.id ?? 0) !== 0,
			},
		],
	});

	if (results.some(result => result.isLoading))
		return <PricingArchitecturePlaceholder />;

	if (results.some(result => result.isError))
		return <Message state="error">
			The PMS inventory could not be loaded at the moment. Please try again later.
		</Message>;

	return <PricingRoomAnchoring rooms={results[0].data || []} architectures={results[1].data || []} />;
});

const PricingRoomAnchoring: React.FunctionComponent<{
	rooms: PricingRoom[],
	architectures: PricingArchitecture[]
}> = ({ rooms, architectures }) => {

	const [selectedRoom, setSelectedRoom] = React.useState<PricingRoom | null>(null);

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();
	const integrationSettingsPath = `/secured/settings/integrations/${companyHotel.id}#pricing`;
	const hiddenRateTypes = companyHotel.priceRecommendationSettings?.hiddenPricingRoomRateTypes ?? [];
	const saveHiddenRateTypesMutation = useSaveHiddenPricingRoomRateTypesMutation(() =>
		toaster.error('Automation', 'Failed to save the rate type filter'));
	const rateTypeCounts = React.useMemo(() => {
		const counts = new Map<string, number>();
		rooms.forEach(room => {
			if (hasText(room.rateType)) counts.set(room.rateType, (counts.get(room.rateType) ?? 0) + 1);
		});
		return [...counts].sort((a, b) => b[1] !== a[1] ? b[1] - a[1] : a[0].localeCompare(b[0]));
	}, [rooms]);
	const visibleRooms = rooms.filter(room => !hasText(room.rateType) || !hiddenRateTypes.includes(room.rateType));
	const hiddenCount = rooms.length - visibleRooms.length;
	const hiddenNames = rateTypeCounts.filter(([type]) => hiddenRateTypes.includes(type)).map(([type]) => type);
	const saveHiddenRateTypes = (rateTypes: string[]) => {
		if (!saveHiddenRateTypesMutation.isPending)
			saveHiddenRateTypesMutation.mutate({ companyHotelId: companyHotel.id, rateTypes });
	};


	return <>
		<PricingArchitectureConfigurationModal
			architectures={architectures}
			pricingRoom={selectedRoom}
			onClose={() => setSelectedRoom(null)}
		/>
		<div className="pt-6 border-t border-gray-200">
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				<div className="col-span-1">
					<div className="flex justify-between">
						<h4 className="text-sm font-semibold text-gray-600 mb-2">PMS Inventory ({rooms.length})</h4>
						<Link to={integrationSettingsPath} className="text-xs text-blue-600 hover:underline font-semibold">Integration settings</Link>
					</div>
					{rateTypeCounts.length > 0 && companyHotel.priceRecommendationSettings !== undefined && companyHotel.priceRecommendationSettings !== null && <div className="mb-2 flex flex-wrap items-center gap-1.5">
						<span className="mr-1 text-xs text-gray-500">Show</span>
						{rateTypeCounts.map(([type, count]) => <ToggleChip key={type} count={count}
							pressed={!hiddenRateTypes.includes(type)} disabled={saveHiddenRateTypesMutation.isPending}
							onToggle={() => saveHiddenRateTypes(hiddenRateTypes.includes(type)
								? hiddenRateTypes.filter(hidden => hidden !== type)
								: [...hiddenRateTypes, type])}>{type}</ToggleChip>)}
					</div>}
					{hiddenCount > 0 && <p className="mb-2 text-xs text-gray-500">{hiddenCount} hidden: {hiddenNames.join(', ')}</p>}
					<div className="mx-auto bg-gray-50 p-3 rounded-lg border border-gray-100">
						{visibleRooms.length > 0 && <ul role="list" className="space-y-3 text-sm leading-6 text-gray-600">
							{visibleRooms.map((room) => (
								<li key={room.id} className="bg-white p-4 rounded-lg shadow">
									<a className="flex items-center justify-between group cursor-pointer"
										onClick={() => setSelectedRoom(room)}>
										<div className="mr-4">
											<div
												className="text-sm font-medium text-gray-900 leading-none group-hover:text-blue-600"><PricingRoomName room={room} /></div>
										</div>
										<div className="flex items-center text-gray-400 group-hover:text-blue-600">
											<PlusIcon className="h-5 w-5" />
										</div>
									</a>
								</li>
							))}
						</ul>}
						{rooms.length > 0 && visibleRooms.length === 0 && <div className="text-center py-8">
							<BuildingOfficeIcon className="mx-auto w-10 h-10 text-gray-400" />
							<h3 className="mt-2 text-sm font-semibold text-gray-900">All remaining rate plans are hidden</h3>
							<p className="mt-1 text-sm text-gray-500">{hiddenCount} rate plans are hidden.{' '}
								<button type="button" className="font-semibold text-blue-600 hover:underline"
									disabled={saveHiddenRateTypesMutation.isPending} onClick={() => saveHiddenRateTypes([])}>Show all</button>
							</p>
						</div>}
						{_isEmpty(rooms) && <div className="text-center py-8">
							<BuildingOfficeIcon className="mx-auto w-10 h-10 text-gray-400" />
							<h3 className="mt-2 text-sm font-semibold text-gray-900">No more rooms found</h3>
							<p className="mt-1 text-sm text-gray-500">Fetch or Refresh rooms in{' '}<Link to={integrationSettingsPath} className="text-blue-600 hover:underline font-semibold">Integration settings</Link>. An administrator can manage the saved provider connection.</p>
						</div>}
					</div>
				</div>
				<div className="sm:col-span-2">
					<h4 className="text-sm font-semibold text-gray-600 mb-2">Pricing Architecture</h4>
					<div className="mx-auto bg-gray-50 p-3 rounded-lg border border-gray-100">
						<div className="bg-white rounded-lg shadow p-4">
							<div className="flex items-center justify-between">
								<div>
									<h3 className="text-sm font-semibold text-gray-900">Anchoring Tree</h3>
									<p className="mt-0.5 text-xs text-gray-500">Each room is grouped under its master room.</p>
								</div>
							</div>
							<PricingRoomArchitectures architectures={architectures} />
						</div>
					</div>
				</div>
			</div>
		</div>
	</>;
};

const PricingRoomArchitectures: React.FunctionComponent<{
	architectures: PricingArchitecture[]
}> = ({ architectures }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();
	const queryClient = useQueryClient();
	const hasPriceRecommendationModule = companyHotel.hasPriceRecommendationModule();

	const sortedArchitectures = React.useMemo(
		() => [...architectures].sort((a, b) => getArchitectureRank(a, architectures) - getArchitectureRank(b, architectures)),
		[architectures],
	);

	const switchArchitectureOrderMutation = useMutation({
		mutationFn: PricingAutomationService.switchPricingArchitectureOrderIndex,
		onError: (error, variables, context) => {
			toaster.error('Automation', 'Failed to update architecture ranking');
		},
		onSuccess: (architectures) => {
			const queryKey = PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id);
			queryClient.setQueryData<PricingArchitecture[]>(queryKey, architectures);
			toaster.success('Automation', 'Architecture ranking updated successfully');
		},
	});

	const switchArchitectureOrder = (architectureId: number, rank: number) => {
		switchArchitectureOrderMutation.mutate({
			companyHotelId: companyHotel.id,
			architectureId,
			orderIndex: rank,
		});
	};

	const saveCompanyHotelPriceRecommendationSettingsMutation = useSaveCompanyHotelPriceRecommendationSettingsMutation();

	const toggleEnforcePricingArchitectureHierarchy = (value: boolean) => {
		const variables = {
			companyHotelId: companyHotel.id,
			input: { enforcePricingArchitectureHierarchy: value },
		};
		saveCompanyHotelPriceRecommendationSettingsMutation.mutate(variables);
	};


	return <div className="mt-4">
		{_isEmpty(architectures) && <div className="text-center py-8">
			<BuildingOfficeIcon className="mx-auto w-10 h-10 text-gray-400" />
			<h3 className="mt-2 text-sm font-semibold text-gray-900">No anchor configured</h3>
			<p className="mt-1 text-sm text-gray-500">Start by selecting a room from the left to create your first anchor.</p>
		</div>}
		{!_isEmpty(architectures) && <>
			{hasPriceRecommendationModule && companyHotel.priceRecommendationSettings !== undefined && companyHotel.priceRecommendationSettings !== null && <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
				<div className="flex items-center justify-between gap-x-4">
					<div className="flex items-start gap-x-3">
						<div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-600 text-white">
							<LockClosedIcon className="h-5 w-5" />
						</div>
						<div>
							<h3 className="text-sm font-semibold text-gray-900">Enforce price ranking on base room types</h3>
							<p className="mt-1 text-xs text-gray-600">
								Lock a price hierarchy across master rooms. Rank 1 sets the floor.
							</p>
						</div>
					</div>
					<Switcher defaultChecked={companyHotel.priceRecommendationSettings.enforcePricingArchitectureHierarchy}
							isLarge onChange={toggleEnforcePricingArchitectureHierarchy} />
				</div>
			</div>}
			{hasPriceRecommendationModule && companyHotel.priceRecommendationSettings?.enforcePricingArchitectureHierarchy && <div
				className="mb-3 flex items-center justify-between gap-1 whitespace-nowrap text-[10px] font-semibold uppercase tracking-normal text-gray-900 sm:text-xs sm:tracking-wide">
				<div>↑ Cheapest (rank 1)</div>
				<div>Most expensive (rank {architectures.length}) ↓</div>
			</div>}
			<ul role="list" className="space-y-3">
				{sortedArchitectures.map((architecture, index) => <li key={architecture.id}
																		className="rounded-lg border border-blue-200 bg-gray-50">
					<PricingArchitectureFragment
						companyHotel={companyHotel}
						architecture={architecture}
						rank={index + 1}
						rankOptions={architectures.length}
						isRankUpdating={switchArchitectureOrderMutation.isPending}
						onRankChange={switchArchitectureOrder}
					/>
				</li>)}
			</ul>
		</>}
	</div>;
};

const getArchitectureRank = (architecture: PricingArchitecture, architectures: PricingArchitecture[]): number => {
	return architecture.orderIndex ?? architectures.findIndex(item => item.id === architecture.id) + 1;
};

export default PricingRoomAnchoringPage;
